import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import DashboardSidebar from '../../agencyDashboardSidebar';
import { createNewJob } from '../../../../../services/staffingService';
import Step1Customer from './Step1Customer';
import Step2Schedule from './Step2Schedule';
import Step3Review   from './Step3Review';

const STEPS = ['Customer', 'Schedule', 'Review'];

const INIT_SCHEDULE = {
  serviceType:      '',
  hourlyPayRate:    '',
  location:         '',
  formattedAddress: '',
  shifts:           [],
};

const CreateJobDispatchPage = () => {
  const { providerCompanyId } = useParams();
  const history = useHistory();

  const [showSidebar, setShowSidebar] = useState(false);
  const [step, setStep]               = useState(0);  // 0, 1, 2
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [schedule, setSchedule]       = useState(INIT_SCHEDULE);
  const [submitting, setSubmitting]   = useState(false);

  const canAdvance = () => {
    if (step === 0) return !!selectedCustomer;
    if (step === 1) {
      return (
        schedule.serviceType.trim() &&
        schedule.hourlyPayRate &&
        schedule.shifts.length > 0 &&
        schedule.shifts.every((s) => s.date && s.startTime && s.endTime)
      );
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Build ISO datetimes from date + time strings
      const buildISO = (dateStr, timeStr) => {
        const d = new Date(`${dateStr}T${timeStr}`);
        return d.toISOString();
      };

      const jobPayload = {
        serviceType:      schedule.serviceType,
        hourlyPayRate:    Number(schedule.hourlyPayRate),
        location:         schedule.location || schedule.formattedAddress,
        formattedAddress: schedule.formattedAddress,
        shifts: schedule.shifts.map((s) => ({
          date:           buildISO(s.date, s.startTime),
          startTime:      buildISO(s.date, s.startTime),
          endTime:        buildISO(s.date, s.endTime),
          staffsRequired: s.staffsRequired,
        })),
      };

      const resp = await createNewJob(jobPayload, providerCompanyId, selectedCustomer._id);
      const newJobId = resp.data.jobId || resp.data.job?._id || resp.data._id;

      toast.success('Job created successfully');

      if (newJobId) {
        history.push(`/provider-company/${providerCompanyId}/dispatch/job/${newJobId}`);
      } else {
        history.push(`/provider-company/${providerCompanyId}/dispatch`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl p-6">
          {/* Back */}
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/dispatch`)}
            className="mb-6 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back to Dispatch
          </button>

          {/* Title */}
          <h2 className="mb-6 text-2xl font-bold text-foreground">Create New Job</h2>

          {/* Progress bar */}
          <div className="mb-8 flex items-center gap-2">
            {STEPS.map((label, i) => {
              const done   = i < step;
              const active = i === step;
              return (
                <React.Fragment key={i}>
                  <div className="flex flex-col items-center">
                    <motion.div
                      animate={{
                        backgroundColor: done ? '#10b981' : active ? '#CC457D' : undefined,
                        borderColor:     done ? '#10b981' : active ? '#CC457D' : undefined,
                      }}
                      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all ${
                        done   ? 'border-emerald-500 bg-emerald-500' :
                        active ? 'border-brand-pink bg-brand-pink'   :
                                 'border-border bg-background'
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 size={16} className="text-white" />
                      ) : (
                        <span className={`text-xs font-bold ${active ? 'text-white' : 'text-muted-foreground'}`}>
                          {i + 1}
                        </span>
                      )}
                    </motion.div>
                    <span className={`mt-1 text-[11px] font-medium ${active ? 'text-brand-pink' : done ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`mb-4 h-px flex-1 transition-colors ${i < step ? 'bg-emerald-500' : 'bg-border'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step content */}
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.2 }}
              >
                {step === 0 && (
                  <Step1Customer
                    providerCompanyId={providerCompanyId}
                    selectedCustomer={selectedCustomer}
                    onSelect={setSelectedCustomer}
                  />
                )}
                {step === 1 && (
                  <Step2Schedule
                    schedule={schedule}
                    onUpdate={setSchedule}
                  />
                )}
                {step === 2 && (
                  <Step3Review
                    selectedCustomer={selectedCustomer}
                    schedule={schedule}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation buttons */}
          <div className="mt-5 flex justify-between">
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 0 || submitting}
              className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
            >
              <ArrowLeft size={15} />
              Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canAdvance() || submitting}
                className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                Next
                <ArrowRight size={15} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 rounded-lg bg-brand-pink px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-pink/90 disabled:opacity-50"
              >
                {submitting
                  ? <Loader2 size={15} className="animate-spin" />
                  : <CheckCircle2 size={15} />
                }
                {submitting ? 'Creating…' : 'Create Job'}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateJobDispatchPage;
